import { useCallback } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toastConfig } from "config"
import { toast } from "react-toastify"
import { reportUploadFailureFetch, startUploadAttemptFetch, updateAssessmentStatusFetch, uploadAudioFileFetch } from "store/middlewares/assessment.crud"
import { deleteAudioFromDB, saveAudioToDB } from "utils/indexedDBService"
import qc from "keys/queryClient"
import uploadAttemptStatuses from "keys/uploadAttemptStatuses"
import { useDispatch, useSelector } from "react-redux"
import { actions } from "store"

const getAudioDurationFromBlob = (audioBlob) => {
    return new Promise((resolve) => {
        const audioURL = URL.createObjectURL(audioBlob);
        const audio = new Audio(audioURL);

        // מאזין לאירוע טעינת המטא-דאטה של הקובץ
        audio.addEventListener('loadedmetadata', () => {
            // האורך מוחזר בשניות
            const duration = Math.round(audio.duration);
            URL.revokeObjectURL(audioURL); // ניקוי הזיכרון
            resolve(duration);
        });

        // במקרה של שגיאה בטעינת הקובץ, נחזיר 0 כדי לא לתקוע את התהליך
        audio.addEventListener('error', () => {
            URL.revokeObjectURL(audioURL);
            resolve(0);
        });
    });
};

const useUploadFile = () => {
    const queryClient = useQueryClient()
    const dispatch = useDispatch()
    const { user } = useSelector(state => state.user)

    const { mutateAsync: startUploadAttempt } = useMutation({
        mutationFn: startUploadAttemptFetch
    })

    const { mutateAsync: uploadFile } = useMutation({
        mutationFn: uploadAudioFileFetch,
        retry: 1, // ניסיון חוזר אחד בלבד
        retryDelay: 1000,
        networkMode: 'always'
    })

    const { mutateAsync: reportUploadFailure } = useMutation({
        mutationFn: reportUploadFailureFetch
    })

    const uploadRecord = useCallback(async (assessment, blobURL, isBackground = false) => {
        // יצירת מזהה ייחודי לקובץ לפי ה-id של המבדק
        const dbKey = `user_${user.user_id}_assessment_audio_${assessment.id}`

        // 1. איסוף נתונים סטטיים של המכשיר והקובץ
        const fileSizeBytes = blobURL.size;
        const userAgent = navigator.userAgent;
        const isMobile = /Mobi|Android/i.test(userAgent);
        const deviceType = isMobile ? 'Mobile' : 'Desktop';
        const durationSeconds = await getAudioDurationFromBlob(blobURL);

        const baseMetadata = {
            file_size_bytes: fileSizeBytes,
            audio_duration_seconds: durationSeconds,
            device_type: deviceType,
            user_agent: userAgent,
        }

        let uploadStartTime = 0;
        let currentAttemptId = null

        try {
            // 1. גיבוי מיידי של הקובץ לכונן המקומי לפני כל פעולת רשת
            await saveAudioToDB(dbKey, blobURL)

            // 2. עדכון סטטוס ל"בהעלאה"
            // AssessmentUploadAttempt יוצר רשומה עבור העלאה הנכחית בטבלת 
            const { data } = await startUploadAttempt({
                id: assessment.id,
                metadata: {
                    ...baseMetadata,
                    status: uploadAttemptStatuses.STARTED
                }
            });

            currentAttemptId = data.attempt_id

            uploadStartTime = Date.now();

            if (currentAttemptId) {
                // 3. ניסיון העלאה לשרת
                dispatch(actions.addUploadingAssessmentId(assessment.id));
                await uploadFile({
                    id: assessment.id,
                    file: blobURL,
                    metadata: {
                        ...baseMetadata,
                        attempt_id: currentAttemptId,
                        start_time: uploadStartTime
                    }
                })
                // 4. אם הגענו לכאן, ההעלאה הצליחה לחלוטין! אפשר למחוק את הגיבוי מהמחשב
                dispatch(actions.removeUploadingAssessmentId(assessment.id));
                await deleteAudioFromDB(dbKey)

                // 5. רענון הנתונים
                queryClient.invalidateQueries({ queryKey: [qc.assessments] })
                queryClient.invalidateQueries({ queryKey: [qc.dashboard] })
            }

        }

        catch (error) {
            // טיפול ייעודי במקרה של שגיאת התנגשות (409) - המבדק כבר מעודכן בשרת ויש קובץ שעלה
            if (error.response && error.response.status === 409) {
                console.log(`מבדק ${assessment.id} כבר מעודכן בשרת. מוחק את הגיבוי המקומי...`);

                // מחיקת הגיבוי המקומי מהדפדפן
                await deleteAudioFromDB(dbKey);

                // ניקוי מזהה המבדק מניהול המצב
                dispatch(actions.removeUploadingAssessmentId(assessment.id));

                // רענון הנתונים כדי שהטבלה תציג את המצב המעודכן ביותר מהשרת
                queryClient.invalidateQueries({ queryKey: [qc.assessments] });
                queryClient.invalidateQueries({ queryKey: [qc.dashboard] });

                // יציאה שקטה ללא הצגת שגיאה למשתמש וללא דיווח כישלון
                return;
            }

            dispatch(actions.addStuckAssessmentId(assessment.id))

            const duration = uploadStartTime > 0 ? (Date.now() - uploadStartTime) : 0;

            let errorMessage = 'שגיאת רשת כללית';


            if (error.response) {
                errorMessage = `שגיאת שרת ${error.response.status}: ${error.response.statusText}`;
            } else if (error.request) {
                errorMessage = 'אין תגובה מהשרת (Timeout/Network)';
            } else {
                errorMessage = error.message;
            }

            if (!isBackground)
                toast.error("ארעה שגיאת רשת. ההקלטה נשמרה במחשב ותוכלו להעלותה מחדש מהטבלה.", toastConfig)

            if (currentAttemptId)
                await reportUploadFailure({
                    id: assessment.id,
                    metadata: {
                        ...baseMetadata,
                        attempt_id: currentAttemptId,
                        status: 'FAILED',
                        upload_duration_ms: duration,
                        error_message: errorMessage
                    }
                })

        }

    }, [reportUploadFailure, uploadFile, reportUploadFailure, queryClient, user])


    return uploadRecord

}

export default useUploadFile